import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

// import Tooltip from "@atlaskit/tooltip";
import Tooltip from "@atlaskit/tooltip";

import { PriceFinderMessages, PricingPeriod } from "../constants";
import { usePriceFinderStore } from "../store/store";

import { PricingExample } from "./pricing-example";

const inputQuestionStyles = css({
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "1.25rem",
    letterSpacing: ".3px",
    marginInlineEnd: "30px"
});

const radioLabelStyles = css({
    display: "inline-flex",
    marginInlineStart: "-5px",
    padding: "15px 15px 15px 0",
    cursor: "pointer",
    gap: "8px"
});

const radioStyles = css({
    "backgroundColor": "#fafbfc",
    "border": "calc(2px * 12 / 7) solid #DFE1E6",
    "borderRadius": "50%",
    "cursor": "pointer",
    "display": "flex",
    "width": "24px",
    "height": "24px",
    "margin": 0,
    "position": "relative",
    "alignItems": "center",
    "justify-content": "center",
    "flexShrink": 0,
    "outline": "none",
    "transform": "scale(calc(.583333))",
    "transition":
        "border-color 0.2s ease-in-out 0s, background-color 0.2s ease-in-out 0s",
    "verticalAlign": "top",
    "appearance": "none",
    ":focus": {
        borderColor: "#4c9aff"
    },
    ":active, :hover": {
        backgroundColor: "#EBECF0"
    },
    "::after": {
        width: "calc(6.85714px)",
        height: "calc(6.85714px)",
        position: "absolute",
        background: "#fafbfc",
        borderRadius: "50%",
        content: "",
        opacity: 0,
        transition:
            "background-color 0.2s ease-in-out 0s, opacity 0.2s ease-in-out 0s"
    },
    ":checked": {
        "borderColor": "#0052cc",
        "backgroundColor": "#0052cc",
        ":hover": {
            backgroundColor: "#0065ff"
        },
        "::after": {
            opacity: 1
        }
    },
    "@media (min-width: 1024px)": {
        "backgroundColor": "#fff",
        "borderColor": "#0052cc",
        "borderWidth": "2px",
        "transform": "none",
        ":focus-visible": {
            boxShadow: "#0052cc 0px 0px 3px 3px"
        },
        "::after": {
            width: "14px",
            height: "14px",
            backgroundColor: "#0052cc"
        },
        ":checked": {
            "backgroundColor": "#fff",
            ":hover": {
                backgroundColor: "unset"
            }
        }
    }
});

interface PriceFinderInputsProps {
    isMonthlyBillingOnly: boolean;
    showPricingExample: boolean;
    setShowPricingExample: Dispatch<SetStateAction<boolean>>;
    messages: PriceFinderMessages;
}

export const PriceFinderInputs = (props: PriceFinderInputsProps) => {
    const {
        isMonthlyBillingOnly,
        showPricingExample,
        setShowPricingExample,
        messages
    } = props;

    const togglePricingExample = () => setShowPricingExample((prev) => !prev);

    const [{ unitCount, billingCycle }, actions] = usePriceFinderStore();
    const [visualUnitCount, setVisualUnitCount] = useState<number | "">(
        unitCount
    );

    useEffect(() => {
        if (
            visualUnitCount !== "" &&
            visualUnitCount > 0 &&
            !Number.isNaN(visualUnitCount)
        ) {
            actions.updateUnitCount(visualUnitCount);
        }
    }, [visualUnitCount, actions]);

    const handleUnitCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const stringValue = e.target.value;

        if (stringValue === "") return setVisualUnitCount("");

        // validate max number of digits
        // default is 5
        // TODO: make this configurable
        if (stringValue.length > 5) return;

        const numberValue = parseInt(stringValue, 10);

        if (numberValue < 1) return;
        setVisualUnitCount(numberValue);
    };

    const handleUnitCountBlur = () => {
        if (visualUnitCount === "") setVisualUnitCount(unitCount);
    };

    const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        actions.updateBillingCycle(e.target.id as PricingPeriod);
    };

    return (
        <div
            css={css({
                backgroundColor: "#fafbfc",
                borderRadius: "4px",
                boxShadow: "#0000001A 1px 1px 6px",
                padding: "20px",
                margin: "auto",
                maxWidth: "1408px"
            })}
        >
            <div
                css={css({
                    "maxWidth": "400px",
                    "margin": "auto",
                    "@media (min-width: 1024px)": {
                        maxWidth: "100%"
                    }
                })}
            >
                <div
                    css={css({
                        "@media (min-width: 1024px)": {
                            display: "grid",
                            gridTemplateColumns:
                                "1fr minmax(max-content, auto) 1fr"
                        }
                    })}
                >
                    <div
                        css={css({
                            "gridColumn": 2,
                            "@media (min-width: 1024px)": {
                                display: "grid",
                                alignItems: "center",
                                gridTemplateColumns:
                                    "1.5fr 1fr minmax(min-content, .9fr)"
                            }
                        })}
                    >
                        <label css={inputQuestionStyles} htmlFor="unit-count">
                            <FormattedMessage
                                {...messages.unitCountInputText}
                            />
                        </label>
                        <div
                            css={css({
                                "width": "100%",
                                "justifyContent": "center",
                                "margin": "auto",
                                "padding": "16px 0",
                                "display": "inline-flex",
                                "alignItems": "center",
                                "@media (min-width: 1024px)": {
                                    margin: 0,
                                    padding: 0,
                                    width: "fit-content"
                                }
                            })}
                        >
                            <input
                                id="unit-count"
                                type="number"
                                css={css({
                                    "color": "#0052cc",
                                    "fontSize": "1.5rem",
                                    "lineHeight": "32px",
                                    "marginInlineEnd": "5px",
                                    "padding": "8px 6px",
                                    "height": "40px",
                                    "width": "134px",
                                    "border": "2px solid #dfe1e6",
                                    "borderRadius": "4px",
                                    "-moz-appearance": "textfield",
                                    "::-webkit-inner-spin-button, ::-webkit-outer-spin-button":
                                        {
                                            "-webkit-appearance": "none"
                                        }
                                })}
                                value={visualUnitCount}
                                onChange={handleUnitCountChange}
                                onBlur={handleUnitCountBlur}
                                data-event-component="inputNumber"
                                data-event-container="priceBlock"
                                data-event="clicked"
                            />
                            <Tooltip
                                content={
                                    <FormattedMessage
                                        {...messages.unitCountInputTooltip}
                                    />
                                }
                                position="top"
                                delay={300}
                            >
                                <InfoIcon />
                            </Tooltip>
                        </div>
                        {isMonthlyBillingOnly ? (
                            <PricingExampleButton
                                onClick={togglePricingExample}
                                showPricingExample={showPricingExample}
                            />
                        ) : (
                            <div />
                        )}
                    </div>
                    {!isMonthlyBillingOnly && (
                        <div
                            css={css({
                                "gridColumn": 2,
                                "@media (min-width: 1024px)": {
                                    display: "grid",
                                    alignItems: "center",
                                    gridTemplateColumns:
                                        "1.5fr 1fr minmax(min-content, .9fr)"
                                }
                            })}
                        >
                            <div css={[inputQuestionStyles]}>
                                <FormattedMessage
                                    id="Pricing.priceFinderInputs.howOftenBilled"
                                    defaultMessage="How often would you like to be billed?"
                                />
                            </div>
                            <span>
                                <label css={radioLabelStyles} htmlFor="MONTHLY">
                                    <input
                                        css={radioStyles}
                                        type="radio"
                                        id="MONTHLY"
                                        name="billing"
                                        checked={billingCycle === "MONTHLY"}
                                        onChange={handleRadioChange}
                                    />
                                    <FormattedMessage
                                        id="Pricing.priceFinderInputs.monthly"
                                        defaultMessage="Monthly"
                                    />
                                </label>
                                <label
                                    css={[
                                        radioLabelStyles,
                                        css({ paddingInlineStart: "30px" })
                                    ]}
                                    htmlFor="ANNUAL"
                                >
                                    <input
                                        css={radioStyles}
                                        type="radio"
                                        id="ANNUAL"
                                        name="billing"
                                        checked={billingCycle === "ANNUAL"}
                                        onChange={handleRadioChange}
                                    />
                                    <FormattedMessage
                                        id="Pricing.priceFinderInputs.anually"
                                        defaultMessage="Annually"
                                    />
                                </label>
                            </span>
                            <PricingExampleButton
                                onClick={togglePricingExample}
                                showPricingExample={showPricingExample}
                            />
                        </div>
                    )}
                </div>
                <div
                    css={css({
                        "> button": {
                            "display": "block",
                            "margin": 0,
                            "@media (min-width: 1024px)": { display: "none" }
                        }
                    })}
                >
                    {showPricingExample && (
                        <PricingExample messages={messages} />
                    )}
                    <PricingExampleButton
                        onClick={togglePricingExample}
                        showPricingExample={showPricingExample}
                    />
                </div>
            </div>
        </div>
    );
};

export const InfoIcon = () => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            role="presentation"
            tabIndex={0}
        >
            <g fillRule="evenodd">
                <circle fill="#8993A4" cx="12" cy="12" r="10" />
                <circle fill="#fff" cx="12" cy="18" r="1" />
                <path
                    d="M15.89 9.05a3.975 3.975 0 00-2.957-2.942C10.321 5.514 8.017 7.446 8 9.95l.005.147a.992.992 0 00.982.904c.552 0 1-.447 1.002-.998a2.004 2.004 0 014.007-.002c0 1.102-.898 2-2.003 2H12a1 1 0 00-1 .987v2.014a1.001 1.001 0 002.004 0v-.782c0-.217.145-.399.35-.472A3.99 3.99 0 0015.89 9.05"
                    fill="#fff"
                />
            </g>
        </svg>
    );
};

interface PricingExampleButtonProps {
    showPricingExample: boolean;
    onClick: () => void;
}

const PricingExampleButton = (props: PricingExampleButtonProps) => {
    return (
        <button
            css={[
                css({
                    "background": "transparent",
                    "border": 0,
                    "cursor": "pointer",
                    "color": "#0052cc",
                    "fontFamily": "inherit",
                    "fontSize": "1rem",
                    "fontWeight": 500,
                    "lineHeight": "32px",
                    "margin": "0 2px 0 30px",
                    "padding": "0",
                    "textOverflow": "ellipsis",
                    "whiteSpace": "nowrap",
                    "display": "none",
                    ":hover": {
                        color: "#0065ff",
                        textDecoration: "underline",
                        textUnderlineOffset: "4px"
                    },
                    "@media (min-width: 1024px)": {
                        display: "inline-block",
                        padding: "0 10px"
                    }
                })
            ]}
            type="button"
            tabIndex={0}
            onClick={props.onClick}
            data-event-component="linkButton"
            data-event-container="priceBlock"
            data-event="clicked"
        >
            {props.showPricingExample ? (
                <FormattedMessage
                    id="Pricing.priceFinderInputs.hidePricingExample"
                    defaultMessage="Hide pricing example"
                />
            ) : (
                <FormattedMessage
                    id="Pricing.priceFinderInputs.seePricingExample"
                    defaultMessage="See pricing example"
                />
            )}
        </button>
    );
};
